export const RG_STAGE_COLUMNS = [
  { key: 'chunking', label: 'Chk', width: 52 },
  { key: 'pass_1', label: 'P1', width: 52 },
  { key: 'toc_compression', label: 'TOC', width: 52 },
  { key: 'pass_2', label: 'P2', width: 52 },
  { key: 'pass_3', label: 'P3', width: 52 },
  { key: 'pass_4', label: 'P4', width: 52 },
  { key: 'bucketing', label: 'Bkt', width: 52 },
  { key: 'synthesis', label: 'Syn', width: 52 },
  { key: 'format_to_db', label: 'Fmt', width: 52 },
  { key: 'enrichment', label: 'Enr', width: 52 },
  { key: 'site_plan_selection', label: 'Site', width: 52 },
  { key: 'save', label: 'Save', width: 52 },
];

export const RG_STAGE_LABELS = {
  chunking: 'Chunking',
  pass_1: 'Pass 1',
  toc_compression: 'TOC Compression',
  pass_2: 'Pass 2',
  pass_3: 'Pass 3',
  pass_4: 'Pass 4',
  bucketing: 'Bucketing',
  synthesis: 'Synthesis',
  format_to_db: 'Format→DB',
  enrichment: 'Enrichment',
  site_plan_selection: 'Site Plans',
  save: 'Save',
};

const deriveAggregateStatus = (runs) => {
  const statuses = runs.map((r) => r.status).filter(Boolean);
  if (statuses.includes('failed')) return 'failed';
  if (statuses.includes('partial')) return 'partial';
  if (statuses.includes('running')) return 'running';
  if (statuses.length > 0 && statuses.every((s) => s === 'success')) return 'success';
  return statuses[statuses.length - 1] || 'unknown';
};

const num = (value) => {
  const x = Number(value);
  return Number.isFinite(x) ? x : 0;
};

/** Roll up multiple stage_run rows (e.g. one per document) into one row per pipeline stage. */
export const aggregateStageRuns = (stages) => {
  const byStage = {};
  for (const run of stages || []) {
    const key = run?.stage;
    if (!key) continue;
    if (!byStage[key]) {
      byStage[key] = {
        stage: key,
        runs: [],
        items_total: 0,
        items_succeeded: 0,
        items_failed: 0,
        items_skipped: 0,
        attempts_total: 0,
        input_tokens: 0,
        output_tokens: 0,
        thinking_tokens: 0,
        cost_usd: 0,
        duration_ms: 0,
        summaries: [],
      };
    }
    const agg = byStage[key];
    agg.runs.push(run);
    agg.items_total += num(run.items_total);
    agg.items_succeeded += num(run.items_succeeded);
    agg.items_failed += num(run.items_failed);
    agg.items_skipped += num(run.items_skipped);
    agg.attempts_total += num(run.attempts_total);
    agg.input_tokens += num(run.input_tokens) || num(run.summary?.cost?.input_tokens);
    agg.output_tokens += num(run.output_tokens) || num(run.summary?.cost?.output_tokens);
    agg.thinking_tokens += num(run.thinking_tokens) || num(run.summary?.cost?.thinking_tokens);
    agg.cost_usd += num(run.cost_usd) || num(run.summary?.cost?.total_cost);
    agg.duration_ms += num(run.duration_ms);
    if (run.summary && typeof run.summary === 'object' && Object.keys(run.summary).length > 0) {
      agg.summaries.push(run.summary);
    }
  }

  const ordered = [];
  for (const col of RG_STAGE_COLUMNS) {
    const agg = byStage[col.key];
    if (!agg) continue;
    agg.run_count = agg.runs.length;
    agg.status = deriveAggregateStatus(agg.runs);
    agg.summary = agg.summaries.length === 1 ? agg.summaries[0] : null;
    ordered.push(agg);
  }
  for (const [key, agg] of Object.entries(byStage)) {
    if (RG_STAGE_COLUMNS.some((col) => col.key === key)) continue;
    agg.run_count = agg.runs.length;
    agg.status = deriveAggregateStatus(agg.runs);
    agg.summary = agg.summaries.length === 1 ? agg.summaries[0] : null;
    ordered.push(agg);
  }
  return ordered;
};

export const buildStageMap = (stages) => {
  const map = {};
  for (const agg of aggregateStageRuns(stages)) {
    map[agg.stage] = agg;
  }
  return map;
};

export const formatInt = (n) => {
  if (n === null || n === undefined || n === '') return '';
  const x = Number(n);
  if (!Number.isFinite(x)) return '';
  return Math.trunc(x).toLocaleString();
};

export const formatUsd = (usd) => {
  const x = Number(usd);
  if (!Number.isFinite(x) || x <= 0) return '';
  if (x < 0.01) return `$${x.toFixed(4)}`;
  return `$${x.toFixed(2)}`;
};

export const formatTokens = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '';
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return `${Math.trunc(x)}`;
};

export const formatDuration = (ms) => {
  const x = Number(ms);
  if (!Number.isFinite(x) || x <= 0) return '';
  if (x < 1000) return `${Math.trunc(x)}ms`;
  if (x < 60_000) return `${(x / 1000).toFixed(1)}s`;
  return `${(x / 60_000).toFixed(1)}m`;
};

export const getStageDetailSummary = (stage) => {
  const summary = stage?.summary || (stage?.summaries?.length === 1 ? stage.summaries[0] : null);
  const stageName = stage?.stage;

  if (stageName === 'enrichment') {
    const s = summary || {};
    if (s.total || s.geocoded != null) {
      const parts = [`geo ${formatInt(s.geocoded)}/${formatInt(s.total)}`];
      if (s.parcel_geometries_resolved != null) {
        parts.push(`parcel reps ${formatInt(s.parcel_geometries_resolved)}/${formatInt(s.total)}`);
      }
      if (s.parcel_lookup_total != null && Number(s.parcel_lookup_total) > 0) {
        parts.push(`lookups ${formatInt(s.parcel_lookup_ok)}/${formatInt(s.parcel_lookup_total)}`);
      }
      return parts.join(', ');
    }
  }

  if (stageName === 'site_plan_selection') {
    const s = summary || {};
    const parts = [];
    if (s.processed != null) parts.push(`${formatInt(s.processed)} processed`);
    if (s.selected != null) parts.push(`${formatInt(s.selected)} selected`);
    if (s.rejected != null) parts.push(`${formatInt(s.rejected)} rejected`);
    if (s.errors) parts.push(`${formatInt(s.errors)} errors`);
    if (parts.length > 0) return parts.join(', ');
  }

  if (stageName === 'chunking') {
    if (stage.run_count > 1) return `${stage.run_count} documents`;
    if (summary?.source_file) return summary.source_file;
  }

  if (stageName === 'bucketing' && summary) {
    const parts = [];
    if (summary.total_buckets != null) parts.push(`${formatInt(summary.total_buckets)} buckets`);
    if (summary.merge_groups != null) parts.push(`${formatInt(summary.merge_groups)} merge groups`);
    if (summary.standalone != null) parts.push(`${formatInt(summary.standalone)} standalone`);
    if (parts.length > 0) return parts.join(', ');
  }

  if (stageName === 'synthesis' && summary) {
    const parts = [];
    if (summary.total_reports != null) parts.push(`${formatInt(summary.total_reports)} reports`);
    if (summary.merged != null) parts.push(`${formatInt(summary.merged)} merged`);
    if (summary.standalone != null) parts.push(`${formatInt(summary.standalone)} standalone`);
    if (parts.length > 0) return parts.join(', ');
  }

  if (stageName === 'format_to_db' && summary) {
    if (summary.total_reports != null) return `${formatInt(summary.total_reports)} formatted`;
  }

  if (stageName === 'save' && summary) {
    const parts = [];
    if (summary.deleted_orphans != null) parts.push(`${formatInt(summary.deleted_orphans)} orphans deleted`);
    if (summary.errors) parts.push(`${formatInt(summary.errors)} errors`);
    if (parts.length > 0) return parts.join(', ');
  }

  if (stageName === 'toc_compression' && summary?.skipped) {
    return summary.skip_reason || 'skipped';
  }

  if (stage.run_count > 1) return `${stage.run_count} runs`;
  return '';
};

export const getRunDetailSummary = (run) => {
  if (run?.summary?.source_file) return run.summary.source_file;
  return getStageDetailSummary({ ...run, run_count: 1, summaries: run.summary ? [run.summary] : [] });
};

export const formatStageShort = (stage) => {
  if (!stage) return '';
  if (stage.status === 'running') return '…';
  const ok = Number(stage.items_succeeded || 0);
  const fail = Number(stage.items_failed || 0);
  const skip = Number(stage.items_skipped || 0);
  const total = Number(stage.items_total || 0);
  if (!total && !ok && !fail && !skip) return '';
  let text = formatInt(ok || total);
  if (fail > 0) text += ` f${formatInt(fail)}`;
  if (skip > 0) text += ` s${formatInt(skip)}`;
  return text;
};

export const stageTooltip = (stage) => {
  if (!stage) return '';
  const label = RG_STAGE_LABELS[stage.stage] || stage.stage;
  const lines = [
    `${label} (${stage.status || 'unknown'})`,
    `total=${formatInt(stage.items_total)} ok=${formatInt(stage.items_succeeded)} skip=${formatInt(stage.items_skipped)} fail=${formatInt(stage.items_failed)}`,
    `attempts=${formatInt(stage.attempts_total)} in=${formatTokens(stage.input_tokens)} out=${formatTokens(stage.output_tokens)} think=${formatTokens(stage.thinking_tokens)}`,
    `cost=${formatUsd(stage.cost_usd) || '$0'} time=${formatDuration(stage.duration_ms) || '—'}`,
  ];
  if (stage.run_count > 1) lines.push(`runs=${stage.run_count}`);
  const detail = getStageDetailSummary(stage);
  if (detail) lines.push(detail);
  if (stage.error_message) lines.push(`error=${stage.error_message}`);
  if (stage.summary && Object.keys(stage.summary).length > 0) {
    lines.push(`summary=${JSON.stringify(stage.summary)}`);
  }
  return lines.join('\n');
};

export const getReportsSaved = (task, stageMap) => {
  const fromStats = Number(task?.reports_saved);
  if (Number.isFinite(fromStats) && fromStats > 0) return fromStats;
  const fromTask = Number(task?.total_reports_generated);
  if (Number.isFinite(fromTask) && fromTask > 0) return fromTask;
  const saveStage = stageMap?.save;
  const fromSave = Number(saveStage?.items_succeeded);
  if (Number.isFinite(fromSave) && fromSave > 0) return fromSave;
  return 0;
};

export const getTotalTokens = (task) => (
  (Number(task?.total_input_tokens) || 0)
  + (Number(task?.total_output_tokens) || 0)
  + (Number(task?.total_thinking_tokens) || 0)
);